import { useEffect, useRef } from 'react'
import type { JSX, ReactNode } from 'react'

/**
 * ══ GỢI Ý KHI RÊ CHUỘT — `InfoTip` · `HoverDetails` · `LitHover` ════════════════════════════════
 *
 * Chép `proship-trans/src/components/ui.tsx:76-155` (G2). Chú thích gốc giữ nguyên.
 *
 * ⚠ `InfoTip` ĐANG LÀM HỎNG CÔNG CỤ ĐO NẾU ĐỌC DOM BẰNG `textContent` — bảng giải thích nằm sẵn
 *   trong DOM, chỉ ẩn bằng `hidden group-hover:block`. `scripts/ui-parity/capture.ts` vì thế dùng
 *   `innerText`. Ghi ở đây để lần sau ai viết lưới đo khỏi vấp lại (đã vấp một lần, 11/09).
 */

/** `ui.tsx:76`. */
export const InfoTip = (
  { text, className = '', align = 'center' }: { text: ReactNode; className?: string; align?: 'center' | 'left' | 'right' },
): JSX.Element => {
  const pos = align === 'right' ? 'right-0' : align === 'left' ? 'left-0' : 'left-1/2 -translate-x-1/2'
  return (
    <span className={`relative inline-flex group align-middle ${className}`}>
      <span className="grid place-items-center w-4 h-4 rounded-full bg-slate-200 text-slate-500 text-[10px] font-bold cursor-help select-none hover:bg-brand-100 hover:text-brand-600">
        i
      </span>
      <span className={`pointer-events-none absolute top-full z-[60] mt-1 hidden group-hover:block w-max max-w-[18rem] whitespace-normal normal-case rounded-lg bg-slate-800 text-white text-[11px] font-normal leading-snug px-2.5 py-1.5 shadow-lg text-left tracking-normal ${pos}`}>
        {text}
      </span>
    </span>
  )
}

/**
 * ⚠ CHỈ GẮN RÊ CHUỘT KHI THIẾT BỊ THẬT SỰ CÓ CHUỘT.
 * Màn cảm ứng giả lập chuột: một cú CHẠM bắn `mouseenter` (mở) rồi `click` (đóng) ⇒ menu KHÔNG
 * mở được. Đã bắt được lỗi này lúc bấm thử 07/08 — đừng bỏ chốt này.
 */
export const canHoverDevice = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches === true

/**
 * ══ MENU THẢ XUỐNG: THÊM MỞ BẰNG RÊ CHUỘT (user chốt 07/08) ═════════════════════════════════
 * Menu kiểu "⬇ Xuất bảng kê (Excel) ▾" trước đây phải BẤM mới bung. Nay rê chuột vào là ra luôn.
 *
 * ⚠ CHỈ THÊM, KHÔNG BỚT (user dặn rõ): đây vẫn là thẻ <details>/<summary> gốc, nên
 *   BẤM để mở/đóng · bàn phím · chọn xong đóng — TẤT CẢ giữ nguyên như trước, không đụng gì.
 *   Rê chuột chỉ là đường vào THỨ HAI. Máy cảm ứng (không có rê chuột) vẫn bấm bình thường.
 *
 * TRỄ 220ms KHI RỜI CHUỘT — giữa nút và bảng menu có khe hở 4px; chuột đi qua khe là mouseleave
 * bắn ra, không có trễ thì menu đóng sập ngay giữa chừng. Đừng bỏ con số này.
 *
 * Dùng cho DROPDOWN NỔI (menu chọn việc). KHÔNG dùng cho <details> kiểu accordion (khối nội dung
 * gấp/mở) — rê qua mà bung cả khối nội dung là rối mắt.
 */
export const HoverDetails = (
  { className = '', children, autoClose = false }: {
    className?: string; children: ReactNode
    /**
     * Menu THAO TÁC kiểu `StmtRowMenu` bản cũ (`Az.tsx:18550-18560`): bấm RA NGOÀI thì đóng (`mousedown` ngoài menu) và chọn
     * MỘT MỤC (nút / liên kết trong menu) cũng đóng (`pick`). Thẻ <details> gốc không tự làm hai việc này (V00437).
     */
    autoClose?: boolean
  },
): JSX.Element => {
  const ref = useRef<HTMLDetailsElement>(null)
  const closeT = useRef<number | undefined>(undefined)
  /** đã BẤM để giữ menu lại → rời chuột không đóng */
  const pinned = useRef(false)
  const hoverOk = canHoverDevice()
  useEffect(() => () => { window.clearTimeout(closeT.current) }, [])
  useEffect(() => {
    if (!autoClose) return undefined
    const onDown = (e: MouseEvent): void => {
      const el = ref.current
      if (el === null || !el.open || el.contains(e.target as Node)) return
      pinned.current = false
      el.open = false
    }
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('mousedown', onDown) }
  }, [autoClose])
  return (
    <details
      ref={ref}
      className={className}
      onClick={autoClose ? (e) => {
        const tgt = e.target as HTMLElement
        if (tgt.closest('summary') !== null || tgt.closest('button, a') === null) return
        pinned.current = false
        if (ref.current !== null) ref.current.open = false
      } : undefined}
      /* ⚠ Chuột luôn RÊ QUA trước khi bấm ⇒ menu đã mở sẵn, cú bấm sẽ thành ĐÓNG ⇒ người dùng
         thấy "bấm không ra menu", mất luôn thao tác bấm cũ. Nên: menu đang mở do RÊ mà bấm thì
         CHẶN toggle, chuyển sang trạng thái GHIM (bấm lần nữa mới đóng). */
      onClickCapture={hoverOk ? (e) => {
        const el = ref.current
        if (el === null || (e.target as HTMLElement).closest('summary') === null) return
        if (el.open && !pinned.current) { e.preventDefault(); pinned.current = true }
        else pinned.current = false
      } : undefined}
      onMouseEnter={hoverOk ? () => { window.clearTimeout(closeT.current); if (ref.current !== null) ref.current.open = true } : undefined}
      onMouseLeave={hoverOk ? () => {
        if (pinned.current) return
        closeT.current = window.setTimeout(() => { if (ref.current !== null) ref.current.open = false }, 220)
      } : undefined}
      onToggle={() => { if (ref.current !== null && !ref.current.open) pinned.current = false }}
    >
      {children}
    </details>
  )
}

/**
 * `ui.tsx:142`. Rê chuột vào nội dung → tooltip hiện NGAY (không chờ ~1s như `title` mặc định của
 * trình duyệt). Dùng cho ô "thành tiền, rê xem số lít" và mọi ô cần chi tiết phụ khi hover.
 */
export const LitHover = (
  { text, children }: { text?: string; children: ReactNode },
): JSX.Element => {
  if (text === undefined || text === '') return <>{children}</>
  return (
    <span className="relative group cursor-help inline-block">
      {children}
      <span className="pointer-events-none absolute right-0 top-full z-[60] mt-0.5 hidden group-hover:block whitespace-nowrap normal-case tracking-normal rounded-md bg-slate-800 text-white text-[10px] font-normal px-1.5 py-0.5 shadow-lg">
        {text}
      </span>
    </span>
  )
}
